import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';

const GlobalSearch = ({ availableOptions, filterLabels, filters, setFilters }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const dropdownRef = useRef(null);

  // Flatten all options with category info
  const allOptions = Object.entries(availableOptions).flatMap(([key, options]) => 
    (Array.isArray(options) ? options : []).map(opt => {
      const optVal = typeof opt === 'object' && opt !== null ? (opt.value ?? '') : opt;
      const optLbl = typeof opt === 'object' && opt !== null ? (opt.label ?? optVal) : opt;
      return {
        key,
        label: filterLabels[key] || key,
        value: String(optVal).trim(),
        displayLabel: String(optLbl).trim()
      };
    })
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = allOptions
          .filter(opt => opt.displayLabel.toLowerCase().includes(lowerSearch) || opt.value.toLowerCase().includes(lowerSearch))
          .slice(0, 20); // Limit suggestions
        setSuggestions(filtered);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, allOptions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

const handleSelect = (key, value) => {
  // 🎯 CONVIERTE NÚMEROS correctamente
  const cleanValue = String(value).trim();
  
  console.log(`🔍 GlobalSearch → ${key}=${cleanValue}`);
  
  setFilters(prev => {
    const currentVals = prev[key] || [];
    const newVals = currentVals.includes(cleanValue) 
      ? currentVals.filter(v => v !== cleanValue)  // Toggle off
      : [...currentVals, cleanValue];              // Toggle on
    
    console.log(`✅ ATÓMICO ${key}:`, newVals.slice(0, 3));
    return { ...prev, [key]: newVals };
  });
  
  setSearchTerm('');
  setShowDropdown(false);
  setSelectedCategory(null);
};

  const handleApply = () => {
    if (selectedCategory && searchTerm.trim()) {
      const value = searchTerm.trim();
      handleSelect(selectedCategory.key, value);
    } else if (searchTerm.trim()) {
      handleSelect('SEARCH', searchTerm.trim());
    }
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.toLowerCase().split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} style={{background: '#ffeb3b', color: '#000'}}>{part}</mark>
        : part
    );
  };

  return (
    <div className="global-search-container" ref={dropdownRef}>
      <div className="global-search-input-wrapper">
        <Search size={18} className="global-search-icon" />
        <input
          type="text"
          className="global-search-input"
          placeholder="Buscar en todos los filtros... (ej: anais)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleApply();
            if (e.key === 'Escape') {
              setSearchTerm('');
              setShowDropdown(false);
            }
          }}
        />
        <button 
          className="global-search-apply-btn"
          onClick={handleApply}
          disabled={!searchTerm.trim()}
          title="Aplicar filtro"
        >
          🔍
        </button>
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className="global-search-dropdown">
          <div className="suggestions-list">
            {suggestions.map((opt, index) => (
              <div 
                key={`${opt.key}-${index}`}
                className={`suggestion-item ${selectedCategory?.key === opt.key ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedCategory(opt);
                  setSearchTerm(opt.value);
                }}
              >
                <div className="suggestion-category">{opt.label}:</div>
                <div className="suggestion-value">
                  {highlightMatch(opt.displayLabel || opt.value, searchTerm)}
                  {filters[opt.key]?.includes(opt.value) && <span className="suggestion-active">✓</span>}
                </div>
              </div>
            ))}
          </div>
          {suggestions.length === 20 && (
            <div className="suggestions-footer">
              Mostrando top 20 resultados...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
